import mixpanel from "mixpanel-browser";

const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;
const IS_DEV = process.env.NODE_ENV === "development";
const TRACKING_ALLOWED_HOSTNAMES = new Set(["dentaris.digitaport.com", "app.dentaris.mx"]);

let isInitialized = false;

const isTrackingAllowed = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return TRACKING_ALLOWED_HOSTNAMES.has(window.location.hostname);
};

const UUID_SEGMENT_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const NUMBER_SEGMENT_REGEX = /^\d+$/;
const OBJECT_ID_SEGMENT_REGEX = /^[a-f0-9]{24}$/i;
const LONG_DYNAMIC_SEGMENT_REGEX = /^(?=.*\d)[a-z0-9_-]{10,}$/i;

const getBrowserName = (userAgent) => {
  if (/Edg\//.test(userAgent)) return "Edge";
  if (/OPR\//.test(userAgent)) return "Opera";
  if (/Chrome\//.test(userAgent) && !/Chromium\//.test(userAgent)) return "Chrome";
  if (/Safari\//.test(userAgent) && !/Chrome\//.test(userAgent)) return "Safari";
  if (/Firefox\//.test(userAgent)) return "Firefox";
  return "Other";
};

const getOperatingSystem = (userAgent) => {
  if (/Windows/i.test(userAgent)) return "Windows";
  if (/Mac OS X|Macintosh/i.test(userAgent)) return "macOS";
  if (/Android/i.test(userAgent)) return "Android";
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "iOS";
  if (/Linux/i.test(userAgent)) return "Linux";
  return "Other";
};

const getDeviceType = (userAgent) => {
  if (/iPad|Tablet|PlayBook|Silk/i.test(userAgent)) return "tablet";
  if (/Mobile|Android|iPhone|iPod/i.test(userAgent)) return "mobile";
  return "desktop";
};

const normalizePathForViews = (path) => {
  if (!path) return "/";

  const normalizedSegments = path
    .split("/")
    .filter(Boolean)
    .map((segment) => {
      if (
        NUMBER_SEGMENT_REGEX.test(segment) ||
        UUID_SEGMENT_REGEX.test(segment) ||
        OBJECT_ID_SEGMENT_REGEX.test(segment) ||
        LONG_DYNAMIC_SEGMENT_REGEX.test(segment)
      ) {
        return ":id";
      }

      return segment;
    });

  return `/${normalizedSegments.join("/")}`;
};

const getDefaultEventProperties = () => {
  const userAgent = navigator.userAgent || "";

  return {
    app: "frontend",
    environment: process.env.NODE_ENV || "development",
    browser_name: getBrowserName(userAgent),
    operating_system: getOperatingSystem(userAgent),
    device_type: getDeviceType(userAgent),
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language || "unknown"
  };
};

export const initMixpanelTracking = () => {
  if (isInitialized || !MIXPANEL_TOKEN || !isTrackingAllowed()) {
    return;
  }

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: IS_DEV,
    track_pageview: false,
    persistence: "localStorage"
  });

  isInitialized = true;
};

export const identifyMixpanelUser = (user) => {
  if (!isInitialized || !user?.id || !isTrackingAllowed()) {
    return;
  }

  const distinctId = String(user.id);
  mixpanel.identify(distinctId);

  mixpanel.people.set({
    $name: [user.firstName, user.lastName].filter(Boolean).join(" ") || undefined,
    $email: user.email,
    organization_id: user.organizationId,
    role: user.role
  });
};

export const trackMixpanelEvent = (eventName, properties = {}) => {
  if (!isInitialized || !isTrackingAllowed()) {
    return;
  }

  mixpanel.track(eventName, {
    ...getDefaultEventProperties(),
    ...properties
  });
};

export const trackViewVisited = (path, properties = {}) => {
  const normalizedPath = normalizePathForViews(path);

  trackMixpanelEvent("View Visited", {
    view_path: path,
    view_name: normalizedPath,
    ...properties
  });
};
